"use client";

import { useEffect, useRef } from "react";

/**
 * Owns the refs a `MediaRecorder` turn needs, plus the teardown that keeps an
 * abandoned recording from outliving its component.
 *
 * `cancelledRef` is the latch the `onstop` handler checks before uploading: a
 * recording can outlive a fast unmount (navigating away mid-record), and the
 * handler must not upload or set state for a component that is gone.
 *
 * The latch is re-armed on every effect run, not just set on teardown. React
 * StrictMode mounts, unmounts, then remounts while *preserving refs*, so a
 * latch that is only ever set to `true` stays `true` for the rest of the
 * component's life and silently drops every recording in dev.
 */
export function useRecorderTeardown(onTeardown?: () => void) {
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const cancelledRef = useRef(false);

  const onTeardownRef = useRef(onTeardown);
  useEffect(() => {
    onTeardownRef.current = onTeardown;
  });

  useEffect(() => {
    cancelledRef.current = false;
    return () => {
      cancelledRef.current = true;
      // These are mutable instance refs, not React element refs: the caller
      // writes them when a recording starts. Reading the latest value at
      // teardown is the point, so the exhaustive-deps "copy it into a variable
      // inside the effect" advice would defeat the cleanup.
      // eslint-disable-next-line react-hooks/exhaustive-deps
      if (timerRef.current) clearTimeout(timerRef.current);
      if (mediaRecorderRef.current?.state === "recording") {
        // eslint-disable-next-line react-hooks/exhaustive-deps
        mediaRecorderRef.current.stop(); // onstop stops the getUserMedia tracks
      }
      onTeardownRef.current?.();
    };
  }, []);

  return { mediaRecorderRef, chunksRef, timerRef, cancelledRef };
}
